import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
// FR lexicon for the Platform overview page. EN untouched (rendered as-is).
// Non-breaking space before % and as thousands separator per FR typography.
// "Insights" is kept as the ATNA module's proper name; the descriptive
// "Insights only" / "Insights plus authorized actions" rows ARE translated.
const FR: Record<string, string> = {
  // Hero diagram grid tiles
  'Digital Card': 'Carte numérique',
  Communications: 'Communications',
  Requests: 'Requêtes',
  Collect: 'Collecte',
  Identity: 'Identité',
  'Smart Water': 'Compteurs intelligents',
  Analytics: 'Analytique',
  Mobile: 'Mobile',
  Portal: 'Portail',
  // Comparison table rows (descriptive — translated)
  'Added on top': 'Ajoutée par-dessus',
  'Anchored in verified identity': "Ancrée dans l'identité vérifiée",
  'Anonymous data': 'Données anonymes',
  'Verified profiles': 'Profils vérifiés',
  'Generic responses': 'Réponses génériques',
  'Context-aware responses': 'Réponses contextuelles',
  'Insights only': 'Analyses seulement',
  'Insights plus authorized actions': 'Analyses et actions autorisées',
  'Questionable trust': 'Confiance incertaine',
  'Verification layer': 'Couche de vérification',
  // Proof stats
  '88k+': '88\u00A0266',
  'Verified Identities': 'Identités vérifiées',
  'City of Brossard': 'Ville de Brossard',
  '31%': '31\u00A0%',
  'Engagement Rate': "Taux d'engagement",
  'In Production Since': 'En production depuis',
  'Continuous operation': 'Exploitation continue',
  // Module grid — names
  Recommendations: 'Recommandations',
  // Module grid — descriptions
  'Verified identity and property relationships.': 'Identité vérifiée et relations de propriété.',
  'Access control and citizen entitlements.': "Contrôle d'accès et droits citoyens.",
  'Streamlined citizen service workflows.': 'Flux de services citoyens simplifiés.',
  'Targeted, verified citizen outreach.': 'Communications citoyennes ciblées et vérifiées.',
  'Integrated payments and registrations.': 'Paiements et inscriptions intégrés.',
  'Real-time anomaly detection and alerts.': "Détection d'anomalies et alertes en temps réel.",
  'Organizational insights and reporting.': 'Analyses organisationnelles et rapports.',
  'Conversational, identity-aware guidance.': "Accompagnement conversationnel fondé sur l'identité.",
  'Context from verified profiles and services.': 'Du contexte issu des profils et services vérifiés.',
  'Personalized next steps for each citizen.': 'Prochaines étapes personnalisées pour chaque citoyen.',
  'Authorized, auditable actions on workflows.': 'Actions autorisées et traçables sur les flux.'
};
// FR lexicon for the radial architecture diagram. Some cells differ from the
// platform grid (e.g. "Smart Water" → "Compteurs" in the narrow radial cell).
const RADIAL_FR: Record<string, string> = {
  // Center
  'Digital Card': 'Carte numérique',
  Profiles: 'Profils',
  Consent: 'Consentement',
  SSO: 'SSO',
  Traceability: 'Traçabilité',
  'Identity Relationships': "Relations d'identité",
  // Outer ring
  'Mobile App': 'Application mobile',
  Web: 'Web',
  Portal: 'Portail',
  'AI Interfaces': 'Interfaces IA',
  'Admin Interfaces': 'Interfaces admin',
  'Analytics Interfaces': 'Interface analytique',
  // Fourth ring
  'Business Systems': "Systèmes d'affaires",
  Recreation: 'Loisirs',
  Library: 'Bibliothèque',
  Taxes: 'Taxes',
  Weather: 'Météo',
  'Open Data': 'Données ouvertes',
  IoT: 'IoT',
  // Third ring
  Communications: 'Communications',
  Requests: 'Requêtes',
  Calendar: 'Calendrier',
  Collect: 'Collecte',
  'Smart Water': 'Compteurs',
  Analytics: 'Analytique',
  // Second ring
  'API Layer': 'Couche API',
  'Business Rules': "Règles d'affaires"
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const PLATFORM_MODULES = [{
  name: 'Identity',
  desc: 'Verified identity and property relationships.',
  to: '/core-infrastructure'
}, {
  name: 'Digital Card',
  desc: 'Access control and citizen entitlements.',
  to: '/digital-card'
}, {
  name: 'Requests',
  desc: 'Streamlined citizen service workflows.',
  to: '/requests'
}, {
  name: 'Communications',
  desc: 'Targeted, verified citizen outreach.',
  to: '/communications'
}, {
  name: 'Collect',
  desc: 'Integrated payments and registrations.',
  to: '/collect'
}, {
  name: 'Smart Water',
  desc: 'Real-time anomaly detection and alerts.',
  to: '/smart-water'
}, {
  name: 'Analytics',
  desc: 'Organizational insights and reporting.',
  to: '/analytics'
}];
const ATNA_MODULES = [{
  name: 'Assistant',
  desc: 'Conversational, identity-aware guidance.',
  to: '/atna'
}, {
  name: 'Insights',
  desc: 'Context from verified profiles and services.',
  to: '/atna'
}, {
  name: 'Recommendations',
  desc: 'Personalized next steps for each citizen.',
  to: '/atna'
}, {
  name: 'Actions',
  desc: 'Authorized, auditable actions on workflows.',
  to: '/atna'
}];
export function Platform() {
  const {
    lang
  } = useLanguage();
  const t = (en: string) => lang === 'FR' ? FR[en] ?? en : en;
  const rt = (en: string) => lang === 'FR' ? RADIAL_FR[en] ?? en : en;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Platform Overview'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-2284">
      <Header data-id="element-2285" />

      <main className="pt-[120px]" data-id="element-2286">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-32 px-6" data-id="element-2287">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2288">
            {/* Text */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-2289">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-6" data-id="element-2290">
                {lang === 'FR' ? 'Plateforme BCITI' : 'BCITI Platform'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-2291">
                {lang === 'FR' ? 'Tout converge vers un noyau vérifié.' : 'Everything connects to a verified core.'}
              </h1>
              <div className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-10 space-y-1" data-id="element-2292">
                <p data-id="element-2293">
                  {lang === 'FR' ? "De l'identité aux services à l'intelligence." : 'From identity to services to intelligence.'}
                </p>
                <p data-id="element-2294">
                  {lang === 'FR' ? 'Chaque couche amplifie la suivante.' : 'Each layer amplifies the next.'}
                </p>
                <p className="pt-3 text-[#121212]/55" data-id="element-2295">
                  {lang === 'FR' ? "La personnalisation n'est pas une fonctionnalité." : 'Personalization is not a feature.'}
                </p>
                <p className="text-[#121212] font-medium" data-id="element-2296">
                  {lang === 'FR' ? "C'est l'architecture." : 'It is the architecture.'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-4" data-id="element-2297">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Platform Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-2298">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2299" />
                </Link>
                <a href="#modules" className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-2300">
                  {lang === 'FR' ? 'Explorer les modules' : 'Explore the Modules'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2301" />
                </a>
              </div>
            </motion.div>

            {/* Hero visual: Identity at center, services around, intelligence throughout */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex justify-center lg:justify-end" data-id="element-2302">
              <div className="w-full max-w-md border border-[#E4E6EC] bg-[#FAFAFB] p-4" data-id="element-2303">
                <div className="border border-[#E4E6EC] bg-white" data-id="element-2304">
                  {/* Intelligence band — embedded throughout */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E6EC]" data-id="element-2305">
                    <div className="flex items-center gap-2" data-id="element-2306">
                      <span className="inline-block w-2.5 h-2.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-2307" />
                      <span className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70" data-id="element-2308">
                        {lang === 'FR' ? "Couche d'intelligence" : 'Intelligence Layer'}
                      </span>
                    </div>
                    <span className="font-sans text-[10px] tracking-wider uppercase text-[#121212]/40" data-id="element-2309">
                      {lang === 'FR' ? 'Intégrée partout' : 'Embedded throughout'}
                    </span>
                  </div>

                  {/* Services around the verified core */}
                  <div className="p-5" data-id="element-2310">
                    <div className="grid grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-2311">
                      {['Digital Card', 'Communications', 'Requests', 'Collect', 'Identity', 'Smart Water', 'Analytics', 'Mobile', 'Portal'].map(tile => {
                      const isCore = tile === 'Identity';
                      return <div key={tile} className={`aspect-square flex flex-col items-center justify-center text-center px-1 ${isCore ? 'bg-[#4F46E5]' : 'bg-white'}`} data-id="element-2312">
                            {isCore && <ShieldCheckIcon className="w-5 h-5 text-white mb-1" strokeWidth={1.75} data-id="element-2313" />}
                            <span className={`font-sans text-[11px] leading-tight ${isCore ? 'text-white font-semibold' : 'text-[#121212]/60'}`} data-id="element-2314">
                              {t(tile)}
                            </span>
                          </div>;
                    })}
                    </div>
                  </div>

                  {/* Verified core label */}
                  <div className="px-5 py-4 border-t border-[#E4E6EC] flex items-center justify-between" data-id="element-2315">
                    <span className="font-sans text-[10px] tracking-wider uppercase text-[#121212]/40" data-id="element-2316">
                      {lang === 'FR' ? 'Noyau vérifié' : 'Verified Core'}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-sans text-[10px] font-semibold tracking-wider uppercase text-[#4F46E5]" data-id="element-2317">
                      <span className="w-1.5 h-1.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-2318" />
                      {lang === 'FR' ? "L'identité au centre" : 'Identity at the center'}
                    </span>
                  </div>
                </div>
                <p className="font-sans text-[11px] text-[#121212]/40 text-center mt-4" data-id="element-2319">
                  {lang === 'FR' ? 'Une seule architecture · Chaque couche connectée' : 'One architecture · Every layer connected'}
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: BCITI ANCHORS AI IN IDENTITY */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2320">
          <div className="max-w-4xl mx-auto" data-id="element-2321">
            <div className="text-center mb-16" data-id="element-2322">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] leading-tight" data-id="element-2323">
                {lang === 'FR' ? <>
                    La plupart des plateformes ajoutent l'IA par-dessus.
                    <br data-id="element-2324" />
                    <span className="text-[#4F46E5]" data-id="element-2325">
                      BCITI ancre l'IA dans l'identité.
                    </span>
                  </> : <>
                    Most platforms add AI on top.
                    <br data-id="element-2326" />
                    <span className="text-[#4F46E5]" data-id="element-2327">
                      BCITI anchors AI in identity.
                    </span>
                  </>}
              </motion.h2>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="bg-white border border-[#E4E6EC] overflow-hidden mb-16" data-id="element-2328">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2329">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-2330">
                  {lang === 'FR' ? 'IA traditionnelle' : 'Traditional AI'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase" data-id="element-2331">
                  BCITI + ATNA
                </div>
              </div>
              {[{
              trad: 'Added on top',
              bciti: 'Anchored in verified identity'
            }, {
              trad: 'Anonymous data',
              bciti: 'Verified profiles'
            }, {
              trad: 'Generic responses',
              bciti: 'Context-aware responses'
            }, {
              trad: 'Insights only',
              bciti: 'Insights plus authorized actions'
            }, {
              trad: 'Questionable trust',
              bciti: 'Verification layer'
            }].map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0" data-id="element-2332">
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-2333">
                    {t(row.trad)}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-2334">
                    {t(row.bciti)}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-center" data-id="element-2335">
              <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-tight" data-id="element-2336">
                {lang === 'FR' ? <>
                    <span className="text-[#121212]/50" data-id="element-2337">
                      La personnalisation n'est pas une fonctionnalité.
                    </span>
                    <br data-id="element-2338" />
                    C'est l'architecture.
                  </> : <>
                    <span className="text-[#121212]/50" data-id="element-2339">
                      Personalization is not a feature.
                    </span>
                    <br data-id="element-2340" />
                    It is the architecture.
                  </>}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: PLATFORM ARCHITECTURE */}
        <section id="architecture" className="py-32 px-6 border-t border-[#E4E6EC] scroll-mt-24 overflow-hidden" data-id="element-2341">
          <span id="stack" className="block -mt-24 pt-24" aria-hidden="true" data-id="element-2342" />
          <div className="max-w-5xl mx-auto" data-id="element-2343">
            <div className="text-center mb-20" data-id="element-2344">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6 leading-tight" data-id="element-2345">
                {lang === 'FR' ? <>
                    Un noyau vérifié.
                    <br data-id="element-2346" />
                    Des services autour.
                    <br data-id="element-2347" />
                    <span className="text-[#4F46E5]" data-id="element-2348">
                      L'intelligence à l'intérieur.
                    </span>
                  </> : <>
                    A verified core.
                    <br data-id="element-2349" />
                    Services around it.
                    <br data-id="element-2350" />
                    <span className="text-[#4F46E5]" data-id="element-2351">Intelligence within.</span>
                  </>}
              </motion.h2>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="w-full overflow-x-auto pb-12 hide-scrollbar flex justify-center" data-id="element-2352">
              <div className="w-[1000px] h-[900px] flex items-center justify-center relative shrink-0" data-id="element-2353">
                <div className="w-[700px] h-[700px] relative" data-id="element-2354">
                  {/* Rings */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full rounded-full border border-[#E4E6EC]" data-id="element-2355" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[84%] h-[84%] rounded-full border border-[#E4E6EC]" data-id="element-2356" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[66%] h-[66%] rounded-full border border-[#E4E6EC]" data-id="element-2357" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[48%] h-[48%] rounded-full border border-[#E4E6EC]" data-id="element-2358" />

                  {/* Center Ring */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[32%] h-[32%] rounded-full border-2 border-[#4F46E5] bg-[#4F46E5]/5 flex flex-col items-center justify-center p-6 text-center z-10 shadow-sm" data-id="element-2359">
                    <div className="font-serif text-xl text-[#4F46E5] mb-4" data-id="element-2360">
                      BCITI ID + ATNA
                    </div>
                    <div className="flex flex-wrap justify-center gap-2" data-id="element-2361">
                      {['Digital Card', 'Profiles', 'Consent', 'SSO', 'Traceability', 'Identity Relationships'].map(item => <span key={item} className="text-[10px] font-sans uppercase tracking-wider text-[#4F46E5]/80 bg-white border border-[#4F46E5]/20 px-2 py-1" data-id="element-2362">
                          {rt(item)}
                        </span>)}
                    </div>
                  </div>

                  {/* Outer Ring */}
                  {['Mobile App', 'Web', 'Portal', 'AI Interfaces', 'Admin Interfaces', 'Analytics Interfaces'].map((item, i, arr) => {
                  const angle = i / arr.length * 2 * Math.PI - Math.PI / 2;
                  const x = 50 + 50 * Math.cos(angle);
                  const y = 50 + 50 * Math.sin(angle);
                  return <div key={item} className="absolute -translate-x-1/2 -translate-y-1/2 bg-white px-4 py-2 text-sm font-sans font-medium tracking-wide text-[#121212]/70 border border-[#E4E6EC] whitespace-nowrap shadow-sm z-20" style={{
                    left: `${x}%`,
                    top: `${y}%`
                  }} data-id="element-2363">
                        {rt(item)}
                      </div>;
                })}

                  {/* Fourth Ring */}
                  {['Business Systems', 'Recreation', 'Library', 'Taxes', 'Weather', 'Open Data', 'IoT'].map((item, i, arr) => {
                  const angle = i / arr.length * 2 * Math.PI - Math.PI / 2;
                  const x = 50 + 42 * Math.cos(angle);
                  const y = 50 + 42 * Math.sin(angle);
                  return <div key={item} className="absolute -translate-x-1/2 -translate-y-1/2 bg-white px-3 py-1.5 text-xs font-sans font-medium tracking-wide text-[#121212]/70 border border-[#E4E6EC] whitespace-nowrap z-20" style={{
                    left: `${x}%`,
                    top: `${y}%`
                  }} data-id="element-2364">
                        {rt(item)}
                      </div>;
                })}

                  {/* Third Ring */}
                  {['Communications', 'Requests', 'Calendar', 'Collect', 'Smart Water', 'Analytics'].map((item, i, arr) => {
                  const angle = i / arr.length * 2 * Math.PI - Math.PI / 2 + Math.PI / 6;
                  const x = 50 + 33 * Math.cos(angle);
                  const y = 50 + 33 * Math.sin(angle);
                  return <div key={item} className="absolute -translate-x-1/2 -translate-y-1/2 bg-white px-3 py-1.5 text-xs font-sans font-medium tracking-wide text-[#121212]/70 border border-[#E4E6EC] whitespace-nowrap z-20" style={{
                    left: `${x}%`,
                    top: `${y}%`
                  }} data-id="element-2365">
                        {rt(item)}
                      </div>;
                })}

                  {/* Second Ring */}
                  {['API Layer', 'Business Rules'].map((item, i) => {
                  const angle = i === 0 ? 0 : Math.PI;
                  const x = 50 + 24 * Math.cos(angle);
                  const y = 50 + 24 * Math.sin(angle);
                  return <div key={item} className="absolute -translate-x-1/2 -translate-y-1/2 bg-white px-3 py-1.5 text-xs font-sans font-medium tracking-wide text-[#4F46E5] border border-[#4F46E5]/30 whitespace-nowrap z-20" style={{
                    left: `${x}%`,
                    top: `${y}%`
                  }} data-id="element-2366">
                        {rt(item)}
                      </div>;
                })}
                </div>
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-center mt-12" data-id="element-2367">
              <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-tight" data-id="element-2368">
                {lang === 'FR' ? <>
                    <span className="text-[#121212]/50" data-id="element-2369">
                      La plupart des plateformes ajoutent l'IA par-dessus.
                    </span>
                    <br data-id="element-2370" />
                    BCITI ancre l'IA au cœur de l'identité.
                  </> : <>
                    <span className="text-[#121212]/50" data-id="element-2371">
                      Most platforms add AI on top.
                    </span>
                    <br data-id="element-2372" />
                    BCITI anchors AI inside the identity core.
                  </>}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: PROOF */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2373">
          <div className="max-w-5xl mx-auto" data-id="element-2374">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-2375">
              {lang === 'FR' ? 'Éprouvé à grande échelle.' : 'Proven at scale.'}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-2376">
              {[{
              stat: '88k+',
              label: 'Verified Identities',
              desc: 'City of Brossard'
            }, {
              stat: '31%',
              label: 'Engagement Rate',
              desc: 'Clarence-Rockland'
            }, {
              stat: '2017',
              label: 'In Production Since',
              desc: 'Continuous operation'
            }].map((metric, i) => <motion.div key={metric.label} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white p-10 text-center" data-id="element-2377">
                  <div className="font-serif text-5xl text-[#121212] mb-4" data-id="element-2378">
                    {t(metric.stat)}
                  </div>
                  <div className="font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase mb-2" data-id="element-2379">
                    {t(metric.label)}
                  </div>
                  <div className="font-sans text-sm text-[#121212]/50" data-id="element-2380">
                    {t(metric.desc)}
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 5: PLATFORM MODULES */}
        <section id="modules" className="py-32 px-6 border-t border-[#E4E6EC] scroll-mt-24" data-id="element-2381">
          <div className="max-w-6xl mx-auto" data-id="element-2382">
            <div className="text-center mb-20" data-id="element-2383">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] leading-tight" data-id="element-2384">
                {lang === 'FR' ? <>
                    Une seule plateforme.
                    <br data-id="element-2385" />
                    <span className="text-[#4F46E5]" data-id="element-2386">
                      Des modules qui se renforcent mutuellement.
                    </span>
                  </> : <>
                    One platform.
                    <br data-id="element-2387" />
                    <span className="text-[#4F46E5]" data-id="element-2388">
                      Modules that reinforce each other.
                    </span>
                  </>}
              </motion.h2>
            </div>

            {/* Grid 1 — BCITI Platform */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="flex items-center gap-3 mb-6" data-id="element-2389">
              <span className="inline-block w-2.5 h-2.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-2390" />
              <h3 className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#121212]/70" data-id="element-2391">
                {lang === 'FR' ? 'Plateforme BCITI' : 'BCITI Platform'}
              </h3>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-16" data-id="element-2392">
              {PLATFORM_MODULES.map((mod, i) => <motion.div key={mod.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className="bg-white p-8 flex flex-col h-full group hover:bg-[#FAFAFB] transition-colors" data-id="element-2393">
                  <div className="flex items-center gap-2 mb-4" data-id="element-2394">
                    <span className="inline-block w-2 h-2 bg-[#4F46E5]" aria-hidden="true" data-id="element-2395" />
                    <h4 className="font-serif text-xl text-[#121212]" data-id="element-2396">
                      {t(mod.name)}
                    </h4>
                  </div>
                  <p className="font-sans text-sm text-[#121212]/60 mb-8 flex-grow" data-id="element-2397">
                    {t(mod.desc)}
                  </p>
                  <Link to={mod.to} className="inline-flex items-center gap-2 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase group-hover:text-[#4F46E5]/80 transition-colors" data-id="element-2398">
                    {lang === 'FR' ? 'Explorer' : 'Explore'}{' '}
                    <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-2399" />
                  </Link>
                </motion.div>)}
            </div>

            {/* Grid 2 — ATNA Intelligence */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="flex items-center gap-3 mb-6" data-id="element-2400">
              <span className="inline-block w-2.5 h-2.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-2401" />
              <h3 className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#121212]/70" data-id="element-2402">
                {lang === 'FR' ? 'Intelligence ATNA' : 'ATNA Intelligence'}
              </h3>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-20" data-id="element-2403">
              {ATNA_MODULES.map((mod, i) => <motion.div key={mod.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className="bg-white p-8 flex flex-col h-full group hover:bg-[#FAFAFB] transition-colors" data-id="element-2404">
                  <div className="flex items-center gap-2 mb-4" data-id="element-2405">
                    <span className="inline-block w-2 h-2 bg-[#4F46E5]" aria-hidden="true" data-id="element-2406" />
                    <h4 className="font-serif text-xl text-[#121212]" data-id="element-2407">
                      {t(mod.name)}
                    </h4>
                  </div>
                  <p className="font-sans text-sm text-[#121212]/60 mb-8 flex-grow" data-id="element-2408">
                    {t(mod.desc)}
                  </p>
                  <Link to={mod.to} className="inline-flex items-center gap-2 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase group-hover:text-[#4F46E5]/80 transition-colors" data-id="element-2409">
                    {lang === 'FR' ? 'Explorer' : 'Explore'}{' '}
                    <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-2410" />
                  </Link>
                </motion.div>)}
            </div>

            {/* Supporting statement */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="border-t border-[#E4E6EC] pt-16 text-center" data-id="element-2411">
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug space-y-1" data-id="element-2412">
                <span className="block" data-id="element-2413">
                  {lang === 'FR' ? "L'identité alimente les communications." : 'Identity powers communications.'}
                </span>
                <span className="block text-[#121212]/70" data-id="element-2414">
                  {lang === 'FR' ? 'Les communications alimentent les services.' : 'Communications power services.'}
                </span>
                <span className="block text-[#4F46E5]" data-id="element-2415">
                  {lang === 'FR' ? "Les services alimentent l'intelligence." : 'Services power intelligence.'}
                </span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: FINAL CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2416">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2417">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8 leading-tight" data-id="element-2418">
              {lang === 'FR' ? 'Bâtissez votre fondation.' : 'Build your foundation.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-12" data-id="element-2419">
              {lang === 'FR' ? "Commencez par l'identité vérifiée. Étendez vers l'intelligence opérationnelle." : 'Start with verified identity. Expand to operational intelligence.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-2420">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Platform Final CTA'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-2421">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2422" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2423" />
    </div>;
}